import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaFilePdf } from 'react-icons/fa';
import { Loader } from '../../utils/tools';
import { getOperationalLogById } from '../../store/actions/operationalLog';
import OperationalLogEntries from './entries';
import ExportOperationalLogToPdf from './exportToPdf';
import CustomModal from '../../hoc/customModal';
import { FACILITY_TYPE_LABELS } from '../../utils/constants/facilityTypes';
import { PLAN_LIMITS } from '../../config/plans';

const AlertIcon = ({ color }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
        <path d="M12 9v4M12 17h.01M10.3 3.9L2.4 18a2 2 0 0 0 1.7 3h16a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
);

const ClockIcon = ({ color }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 3" />
    </svg>
);

const OperationalLogDetail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { operationalLogId } = useParams();

    const logToEdit = useSelector(state => state.operationalLog.logToEdit);
    const loading = useSelector(state => state.operationalLog.logToEditLoading);
    const currentBranch = useSelector(state => state.branch.current);
    const tenant = useSelector(state => state.tenant.current);
    const initialBranchRef = useRef(currentBranch);
    const [isExportModalVisible, setIsExportModalVisible] = useState(false);

    const planLimits = PLAN_LIMITS[tenant?.plan];
    const hasAccess = planLimits?.maxOperationalLogs > 0;

    useEffect(() => {
        if (!hasAccess) {
            navigate('/operational-log');
            return;
        }
        if (operationalLogId) {
            dispatch(getOperationalLogById(operationalLogId)).then((response) => {
                if (response.error) navigate('/operational-log');
            });
        }
    }, [dispatch, operationalLogId, hasAccess, navigate]);

    useEffect(() => {
        if (currentBranch !== initialBranchRef.current) {
            navigate('/operational-log');
        }
    }, [currentBranch, navigate]);

    if (loading || !logToEdit || logToEdit._id !== operationalLogId) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '35vh' }}>
                <Loader />
            </div>
        );
    }

    const consentStatus = logToEdit.consent?.status;
    const isConsentExpired = consentStatus === 'expired';
    const isConsentExpiring = consentStatus === 'expiring';

    return (
        <div>
            {(isConsentExpired || isConsentExpiring) && (
                <div
                    style={{
                        display: 'flex', alignItems: 'flex-start', gap: '12px',
                        backgroundColor: isConsentExpired ? '#F7E4DE' : '#FBEBD8',
                        border: `1px solid ${isConsentExpired ? '#A5432B' : '#C97D2C'}`,
                        borderRadius: '10px', padding: '14px 16px', marginTop: '12px', marginBottom: '16px'
                    }}
                >
                    {isConsentExpired ? <AlertIcon color="#A5432B" /> : <ClockIcon color="#C97D2C" />}
                    <div>
                        <p style={{ fontSize: '13.5px', fontWeight: 600, color: isConsentExpired ? '#A5432B' : '#C97D2C', margin: '0 0 2px' }}>
                            {isConsentExpired
                                ? `Súhlas ${logToEdit.consent?.name} pre tento denník vypršal.`
                                : `Súhlas ${logToEdit.consent?.name} pre tento denník čoskoro vyprší.`}
                        </p>
                        <div style={{ fontSize: '12.5px', color: '#3D5654', lineHeight: 1.5 }}>
                            {isConsentExpired
                                ? 'Odporúčame čo najskôr podať žiadosť o predĺženie na vydávajúcom orgáne.'
                                : 'Ak plánujete predĺženie, odporúčame podať žiadosť s dostatočným predstihom pred vydávajúcim orgánom.'}
                        </div>
                    </div>
                </div>
            )}

            <div className="content p-4 mb-4">
                <div className="d-flex justify-content-end mb-3">
                    <OverlayTrigger placement="left" overlay={<Tooltip>Exportovať do PDF</Tooltip>}>
                        <button className="export-icon-btn" onClick={() => setIsExportModalVisible(true)}>
                            <FaFilePdf size={20} />
                        </button>
                    </OverlayTrigger>
                </div>

                <Row>
                    <Col>
                        <div className="evidence-meta-label">Názov denníka</div>
                        <div><strong>{logToEdit.name}</strong></div>
                    </Col>
                    <Col>
                        <div className="evidence-meta-label">Prevádzka</div>
                        <div><strong>{logToEdit.branch?.name ?? '-'}</strong></div>
                    </Col>
                    <Col>
                        <div className="evidence-meta-label">Typ zariadenia</div>
                        <div><strong>{FACILITY_TYPE_LABELS[logToEdit.facilityType] ?? logToEdit.facilityType}</strong></div>
                    </Col>
                    <Col>
                        <div className="evidence-meta-label">Kód nakladania</div>
                        <div><strong>{logToEdit.loadingCode ?? '-'}</strong></div>
                    </Col>
                    <Col>
                        <div className="evidence-meta-label">Súhlas</div>
                        <div><strong>{logToEdit.consent?.name} ({logToEdit.consent?.decisionNumber})</strong></div>
                    </Col>
                </Row>
            </div>

            <OperationalLogEntries operationalLogId={operationalLogId} facilityType={logToEdit.facilityType} />

            <CustomModal isVisible={isExportModalVisible} onClose={() => setIsExportModalVisible(false)}>
                <ExportOperationalLogToPdf
                    operationalLogId={operationalLogId}
                    name={logToEdit.name}
                    onClose={() => setIsExportModalVisible(false)}
                />
            </CustomModal>
        </div>
    );
};

export default OperationalLogDetail;
